import { api } from './client.js';

/**
 * Every call the console makes, grouped by module. Screens import from here
 * rather than assembling URLs, so a route change is a one-line edit.
 */

export const authApi = {
  login: (identifier, password) => api.post('/auth/login', { identifier, password, client: 'web' }),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  sessions: () => api.get('/auth/sessions'),
  revokeSession: (id) => api.delete(`/auth/sessions/${id}`),
  updateProfile: (body) => api.patch('/auth/profile', body),
};

export const metaApi = {
  enums: () => api.get('/meta'),
};

export const orgApi = {
  lookups: () => api.get('/org/lookups'),
  chart: () => api.get('/org/chart'),
  company: () => api.get('/org/company'),
  updateCompany: (body) => api.patch('/org/company', body),
  /**
   * The signature image, as a blob.
   *
   * Not a plain <img src>: the route needs the bearer token, which the browser
   * will not attach to an image tag. The caller turns this into an object URL
   * and revokes it afterwards.
   */
  signatureBlob: async () => {
    const { blob } = await api.get('/org/company/signature', { raw: true });
    return blob;
  },
  uploadSignature: (file) => {
    const form = new FormData();
    form.append('signature', file);
    return api.upload('/org/company/signature', form);
  },
  deleteSignature: () => api.delete('/org/company/signature'),


  departments: (query) => api.get('/org/departments', { query }),
  createDepartment: (body) => api.post('/org/departments', body),
  updateDepartment: (id, body) => api.patch(`/org/departments/${id}`, body),
  deleteDepartment: (id) => api.delete(`/org/departments/${id}`),

  designations: (query) => api.get('/org/designations', { query }),
  createDesignation: (body) => api.post('/org/designations', body),
  updateDesignation: (id, body) => api.patch(`/org/designations/${id}`, body),
  deleteDesignation: (id) => api.delete(`/org/designations/${id}`),

  locations: (query) => api.get('/org/locations', { query }),
  createLocation: (body) => api.post('/org/locations', body),
  updateLocation: (id, body) => api.patch(`/org/locations/${id}`, body),
  deleteLocation: (id) => api.delete(`/org/locations/${id}`),

  attendancePolicies: () => api.get('/org/attendance-policies'),
  createAttendancePolicy: (body) => api.post('/org/attendance-policies', body),
  updateAttendancePolicy: (id, body) => api.patch(`/org/attendance-policies/${id}`, body),
  deleteAttendancePolicy: (id) => api.delete(`/org/attendance-policies/${id}`),

  shifts: (query) => api.get('/org/shifts', { query }),
  createShift: (body) => api.post('/org/shifts', body),
  updateShift: (id, body) => api.patch(`/org/shifts/${id}`, body),
  deleteShift: (id) => api.delete(`/org/shifts/${id}`),
};

export const employeeApi = {
  list: (query) => api.get('/employees', { query }),
  get: (id) => api.get(`/employees/${id}`),
  summary: (id) => api.get(`/employees/${id}/summary`),
  create: (body) => api.post('/employees', body),
  update: (id, body) => api.patch(`/employees/${id}`, body),
  changeStatus: (id, body) => api.post(`/employees/${id}/status`, body),
  changeEmployeeCode: (id, body) => api.patch(`/employees/${id}/employee-code`, body),
  deactivate: (id) => api.delete(`/employees/${id}`),
  stats: () => api.get('/employees/stats'),
  nextCode: (prefix) => api.get('/employees/next-code', { query: { prefix } }),
  team: (managerId) => api.get('/employees/me/team', { query: { managerId } }),

  uploadAvatar: (id, file) => {
    const form = new FormData();
    form.append('avatar', file);
    return api.upload(`/employees/${id}/avatar`, form);
  },

  assets: (id) => api.get(`/employees/${id}/assets`),
  issueAsset: (id, body) => api.post(`/employees/${id}/assets`, body),
  updateAsset: (id, assetId, body) => api.patch(`/employees/${id}/assets/${assetId}`, body),
  returnAsset: (id, assetId, body) => api.post(`/employees/${id}/assets/${assetId}/return`, body),
  deleteAsset: (id, assetId) => api.delete(`/employees/${id}/assets/${assetId}`),

  createAccount: (id, body) => api.post(`/employees/${id}/account`, body),
  updateAccount: (id, body) => api.patch(`/employees/${id}/account`, body),
};

export const attendanceApi = {
  list: (query) => api.get('/attendance', { query }),
  today: (date) => api.get('/attendance/today', { query: { date } }),
  grid: (period) => api.get('/attendance/grid', { query: { period } }),
  calendar: (period) => api.get('/attendance/calendar', { query: { period } }),
  summary: (employee, period) => api.get('/attendance/summary', { query: { employee, period } }),
  day: (employeeId, dateKey) => api.get(`/attendance/${employeeId}/${dateKey}`),
  edit: (id, body) => api.patch(`/attendance/${id}`, body),
  rebuild: (body) => api.post('/attendance/rebuild', body),
  /**
   * One status across a date range for many people.
   *
   * Pass `dryRun: true` to get the counts back without writing anything -
   * which is what the console does before it asks for confirmation.
   */
  bulkUpdate: (body) => api.post('/attendance/bulk', body),

  punches: (query) => api.get('/attendance/punches', { query }),
  unmatched: () => api.get('/attendance/punches/unmatched'),
  addPunch: (body) => api.post('/attendance/punches/manual', body),
  deletePunch: (id) => api.delete(`/attendance/punches/${id}`),
  mapBiometric: (body) => api.post('/attendance/punches/map', body),

  importCsv: (file, deviceId) => {
    const form = new FormData();
    form.append('file', file);
    if (deviceId) form.append('deviceId', deviceId);
    return api.upload('/attendance/punches/import', form);
  },

  devices: () => api.get('/attendance/devices'),
  createDevice: (body) => api.post('/attendance/devices', body),
  updateDevice: (id, body) => api.patch(`/attendance/devices/${id}`, body),
  deleteDevice: (id) => api.delete(`/attendance/devices/${id}`),

  regularizations: (query) => api.get('/attendance/regularizations', { query }),
  decideRegularization: (id, decision, remarks) =>
    api.post(`/attendance/regularizations/${id}/decision`, { decision, remarks }),
};

export const leaveApi = {
  types: () => api.get('/leave/types'),
  createType: (body) => api.post('/leave/types', body),
  updateType: (id, body) => api.patch(`/leave/types/${id}`, body),
  deleteType: (id) => api.delete(`/leave/types/${id}`),

  balances: (employee, year) => api.get('/leave/balances', { query: { employee, year } }),
  ledger: (id) => api.get(`/leave/balances/${id}/ledger`),
  adjustBalance: (body) => api.post('/leave/balances/adjust', body),

  requests: (query) => api.get('/leave/requests', { query }),
  request: (id) => api.get(`/leave/requests/${id}`),
  apply: (body) => api.post('/leave/requests', body),
  decide: (id, decision, remarks) =>
    api.post(`/leave/requests/${id}/decision`, { decision, remarks }),
  cancel: (id, reason) => api.post(`/leave/requests/${id}/cancel`, { reason }),

  calendar: (query) => api.get('/leave/calendar', { query }),
  stats: (year) => api.get('/leave/stats', { query: { year } }),
  runAccrual: (period) => api.post('/leave/accrual', { period }),
  closeYear: (fromYear, toYear) => api.post('/leave/close-year', { fromYear, toYear }),
};

export const holidayApi = {
  list: (query) => api.get('/holidays', { query }),
  upcoming: (limit) => api.get('/holidays/upcoming', { query: { limit } }),
  create: (body) => api.post('/holidays', body),
  bulkCreate: (body) => api.post('/holidays/bulk', body),
  update: (id, body) => api.patch(`/holidays/${id}`, body),
  remove: (id) => api.delete(`/holidays/${id}`),
};

export const payrollApi = {
  components: () => api.get('/payroll/components'),
  createComponent: (body) => api.post('/payroll/components', body),
  updateComponent: (id, body) => api.patch(`/payroll/components/${id}`, body),
  deleteComponent: (id) => api.delete(`/payroll/components/${id}`),

  previewStructure: (ctc, componentOverrides) =>
    api.post('/payroll/structures/preview', { ctc, componentOverrides }),
  structures: (query) => api.get('/payroll/structures', { query }),
  createStructure: (body) => api.post('/payroll/structures', body),
  myStructure: (employee) => api.get('/payroll/my-structure', { query: { employee } }),

  runs: (query) => api.get('/payroll/runs', { query }),
  run: (id) => api.get(`/payroll/runs/${id}`),
  createRun: (body) => api.post('/payroll/runs', body),
  processRun: (id) => api.post(`/payroll/runs/${id}/process`),
  approveRun: (id) => api.post(`/payroll/runs/${id}/approve`),
  reopenRun: (id, reason) => api.post(`/payroll/runs/${id}/reopen`, { reason }),
  publishRun: (id, notify = true) => api.post(`/payroll/runs/${id}/publish`, { notify }),
  markPaid: (id, body) => api.post(`/payroll/runs/${id}/mark-paid`, body),
  deleteRun: (id) => api.delete(`/payroll/runs/${id}`),

  payslips: (query) => api.get('/payroll/payslips', { query }),
  payslip: (id) => api.get(`/payroll/payslips/${id}`),
  downloadPayslip: (id, filename) => api.download(`/payroll/payslips/${id}/download`, { filename }),
  downloadBundle: (runId, period) =>
    api.download(`/payroll/runs/${runId}/payslips.pdf`, { filename: `Payslips-${period}.pdf` }),

  addAdjustment: (id, body) => api.post(`/payroll/payslips/${id}/adjustments`, body),
  removeAdjustment: (id, adjustmentId) =>
    api.delete(`/payroll/payslips/${id}/adjustments/${adjustmentId}`),

  bankSheet: (runId) => api.get(`/payroll/runs/${runId}/bank-sheet`),
  downloadBankSheet: (runId, period) =>
    api.download(`/payroll/runs/${runId}/bank-sheet`, {
      query: { format: 'csv' },
      filename: `bank-transfer-${period}.csv`,
    }),
  statutoryReport: (period) => api.get('/payroll/reports/statutory', { query: { period } }),
  costTrend: (financialYear) => api.get('/payroll/reports/cost-trend', { query: { financialYear } }),

  taxConfigs: () => api.get('/payroll/tax-config'),
  taxConfig: (fy) => api.get(`/payroll/tax-config/${fy}`),
  saveTaxConfig: (body) => api.put('/payroll/tax-config', body),
};

export const documentApi = {
  list: (query) => api.get('/documents', { query }),
  get: (id) => api.get(`/documents/${id}`),
  stats: () => api.get('/documents/stats'),
  update: (id, body) => api.patch(`/documents/${id}`, body),
  remove: (id) => api.delete(`/documents/${id}`),
  download: (id, filename) => api.download(`/documents/${id}/download`, { filename }),
  acknowledgements: (id) => api.get(`/documents/${id}/acknowledgements`),
  /**
   * Fetches the file itself for on-screen preview.
   *
   * A plain <img src> or <iframe src> cannot be used: the route needs the bearer
   * token, which the browser will not attach to those. So it comes back as a
   * blob and the caller turns it into an object URL — remember to revoke it.
   */
  previewBlob: async (id) => {
    const { blob } = await api.get(`/documents/${id}/download`, {
      query: { inline: 'true' },
      raw: true,
    });
    return blob;
  },


  upload: (fields, file) => {
    const form = new FormData();
    form.append('file', file);
    for (const [key, value] of Object.entries(fields)) {
      if (value === undefined || value === null || value === '') continue;
      form.append(key, Array.isArray(value) ? value.join(',') : value);
    }
    return api.upload('/documents', form);
  },

  bulkForm16: (files, financialYear, notify = true) => {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    form.append('financialYear', financialYear);
    form.append('notify', String(notify));
    return api.upload('/documents/form16/bulk', form);
  },
};

export const dashboardApi = {
  admin: () => api.get('/dashboard/admin'),
  attendanceTrend: (days) => api.get('/dashboard/attendance-trend', { query: { days } }),
  headcount: (year) => api.get('/dashboard/reports/headcount', { query: { year } }),
  leaveReport: (year) => api.get('/dashboard/reports/leave', { query: { year } }),
  employee360: (id) => api.get(`/dashboard/reports/employee/${id}`),

  /** The datasets this user is allowed to export. */
  exports: () => api.get('/dashboard/exports'),

  /** Streams one dataset straight to a file the browser saves. */
  downloadExport: (dataset, { from, to, scope, department, format } = {}) =>
    api.download(`/dashboard/exports/${dataset}`, {
      query: { from, to, scope, department, format },
    }),
};

export const notificationApi = {
  list: (query) => api.get('/notifications', { query }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  remove: (id) => api.delete(`/notifications/${id}`),
};

export const auditApi = {
  list: (query) => api.get('/audit', { query }),
};

export const resignationApi = {
  list: (query) => api.get('/resignations', { query }),
  get: (id) => api.get(`/resignations/${id}`),
  mine: () => api.get('/resignations/mine'),
  submit: (body) => api.post('/resignations', body),
  withdraw: (id) => api.post(`/resignations/${id}/withdraw`),
  review: (id, body) => api.post(`/resignations/${id}/review`, body),
  decide: (id, body) => api.post(`/resignations/${id}/decide`, body),
};

export const inviteApi = {
  code: () => api.get('/invites/code'),
  rotateCode: () => api.post('/invites/code/rotate'),
  list: (query) => api.get('/invites', { query }),
  create: (body) => api.post('/invites', body),
  resend: (id) => api.post(`/invites/${id}/resend`),
  revoke: (id) => api.post(`/invites/${id}/revoke`),
};

export const expenseApi = {
  list: (query) => api.get('/expenses', { query }),
  get: (id) => api.get(`/expenses/${id}`),
  decide: (id, body) => api.post(`/expenses/${id}/decide`, body),
  markPaid: (id, body) => api.post(`/expenses/${id}/pay`, body),
  cancel: (id) => api.post(`/expenses/${id}/cancel`),

  /** The receipt as a blob, for on-screen preview. Revoke the object URL after. */
  receiptBlob: async (id) => {
    const { blob } = await api.get(`/expenses/${id}/receipt`, { query: { inline: 'true' }, raw: true });
    return blob;
  },
  downloadReceipt: (id, filename) => api.download(`/expenses/${id}/receipt`, { filename }),

  create: (fields, file) => {
    const form = new FormData();
    if (file) form.append('receipt', file);
    for (const [k, v] of Object.entries(fields)) {
      if (v === undefined || v === null || v === '') continue;
      form.append(k, v);
    }
    return api.upload('/expenses', form);
  },
};

export const taskApi = {
  list: (query) => api.get('/tasks', { query }),
  get: (id) => api.get(`/tasks/${id}`),
  submit: (body) => api.post('/tasks', body),
  /** Days the signed-in user still owes, and whether they block check-in. */
  pending: () => api.get('/tasks/pending'),
  /** Per-employee figures for one month, optionally one department. */
  compliance: (query) => api.get('/tasks/compliance', { query }),
};

export const wfhApi = {
  list: (query) => api.get('/wfh', { query }),
  get: (id) => api.get(`/wfh/${id}`),
  create: (body) => api.post('/wfh', body),
  cancel: (id) => api.post(`/wfh/${id}/cancel`),
  decide: (id, body) => api.post(`/wfh/${id}/decide`, body),
  days: (query) => api.get('/wfh/days', { query }),
  addDay: (body) => api.post('/wfh/days', body),
  removeDay: (id) => api.delete(`/wfh/days/${id}`),
  today: () => api.get('/wfh/today'),
  punch: (body) => api.post('/wfh/punch', body),
};
